import { Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { TranslateService } from '@ngx-translate/core';
import { BoardColumn, DynamicBoardCard } from '../board-v3.types';
import {
  BOARD_V3_DELETE_DROP_ZONE_ID,
  boardV3DragActionDropZoneId,
  boardV3VisibleDragActionColumns,
} from './drag-action-footer.utils';

export interface BoardV3DragActionDropEvent {
  event: CdkDragDrop<DynamicBoardCard[]>;
  column: BoardColumn;
}

@Component({
  selector: 'app-board-v3-drag-action-footer',
  templateUrl: './drag-action-footer.component.html',
  standalone: false,
})
export class BoardV3DragActionFooterComponent implements OnChanges {
  private readonly translateService = inject(TranslateService);

  @Input() columns: BoardColumn[] = [];
  @Input() boardColumnIds: string[] = [];
  @Input() isDragging: boolean = false;

  @Output() cardDrop = new EventEmitter<BoardV3DragActionDropEvent>();
  @Output() deleteDrop = new EventEmitter<CdkDragDrop<DynamicBoardCard[]>>();

  readonly dragActionCards: DynamicBoardCard[] = [];

  dragActionColumns: BoardColumn[] = [];
  failedDragActionColumns: BoardColumn[] = [];
  successDragActionColumns: BoardColumn[] = [];
  visibleDragActionColumns: BoardColumn[] = [];
  hasMultipleFailedDragActionColumns: boolean = false;
  activeDragActionId: string | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['columns']) {
      this.rebuildDragActionColumns();
    }

    if (changes['isDragging'] && !this.isDragging) {
      this.resetActiveDragAction();
    }
  }

  dragActionGridTemplate(columns: BoardColumn[]): string {
    return `repeat(${Math.max(columns.length + 1, 1)}, minmax(0, 1fr))`;
  }

  get deleteDropZoneId(): string {
    return BOARD_V3_DELETE_DROP_ZONE_ID;
  }

  dragActionDropZoneId(column: BoardColumn): string {
    return boardV3DragActionDropZoneId(column, this.hasMultipleFailedDragActionColumns);
  }

  dragActionConnectedTo(column?: BoardColumn): string[] {
    const currentDropZoneId = column ? this.dragActionDropZoneId(column) : null;

    return [
      ...this.boardColumnIds,
      BOARD_V3_DELETE_DROP_ZONE_ID,
      ...this.visibleDragActionColumns
        .map(actionColumn => this.dragActionDropZoneId(actionColumn))
        .filter(dropZoneId => dropZoneId !== currentDropZoneId),
    ];
  }

  deleteDragActionConnectedTo(): string[] {
    return [
      ...this.boardColumnIds,
      ...this.visibleDragActionColumns.map(column => this.dragActionDropZoneId(column)),
    ];
  }

  dragActionLabel(column: BoardColumn): string {
    if (column.type === 'FAILED' && this.hasMultipleFailedDragActionColumns) {
      return String(this.translateService.instant('board_v3_column_type_failed'));
    }

    return column.name;
  }

  dragActionIcon(column: BoardColumn): string {
    return column.type === 'FAILED' ? 'cancel' : 'check_circle';
  }

  onDragActionEnter(column: BoardColumn): void {
    this.setActiveDragAction(column.id);
  }

  onDragActionExit(column: BoardColumn): void {
    this.clearActiveDragAction(column.id);
  }

  dragActionBackground(column: BoardColumn): string {
    const colorWeight = this.activeDragActionId === column.id ? 58 : 22;
    return `color-mix(in srgb, ${column.color.background} ${colorWeight}%, white)`;
  }

  get deleteDragActionBackground(): string {
    return this.activeDragActionId === BOARD_V3_DELETE_DROP_ZONE_ID ? '#b8bec8' : '#e5e7eb';
  }

  get deleteDragActionBorderColor(): string {
    return this.activeDragActionId === BOARD_V3_DELETE_DROP_ZONE_ID ? '#6b7280' : 'transparent';
  }

  onDeleteDragActionEnter(): void {
    this.setActiveDragAction(BOARD_V3_DELETE_DROP_ZONE_ID);
  }

  onDeleteDragActionExit(): void {
    this.clearActiveDragAction(BOARD_V3_DELETE_DROP_ZONE_ID);
  }

  onDragActionDrop(event: CdkDragDrop<DynamicBoardCard[]>, column: BoardColumn): void {
    this.resetActiveDragAction();
    this.cardDrop.emit({ event, column });
  }

  onDeleteDragActionDrop(event: CdkDragDrop<DynamicBoardCard[]>): void {
    this.resetActiveDragAction();
    this.deleteDrop.emit(event);
  }

  private rebuildDragActionColumns(): void {
    this.failedDragActionColumns = this.columns.filter(column => column.type === 'FAILED');
    this.successDragActionColumns = this.columns.filter(column => column.type === 'SUCCESS');
    this.dragActionColumns = [...this.failedDragActionColumns, ...this.successDragActionColumns];
    this.hasMultipleFailedDragActionColumns = this.failedDragActionColumns.length > 1;
    this.visibleDragActionColumns = boardV3VisibleDragActionColumns(this.columns);
  }

  private setActiveDragAction(actionId: string): void {
    this.activeDragActionId = actionId;
  }

  private clearActiveDragAction(actionId: string): void {
    if (this.activeDragActionId === actionId) {
      this.activeDragActionId = null;
    }
  }

  private resetActiveDragAction(): void {
    this.activeDragActionId = null;
  }
}
